import { getProjectGroup } from "authoring/routes/project/group/controller/get-project-group"
import { updateProjectGroup } from "authoring/routes/project/group/controller/update-project-group"
import { stripChipHtml } from "authoring/routes/macro/lib/macro-chip-utils"
import { getCurrentUser } from "controller/getCurrentUser"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"

export function useGroupAmends({ projectId, groupId } = {}) {
    const { data: group = null, loading = false } = getProjectGroup.useResults.status({ projectId, groupId }) ?? {}
    const currentUser = getCurrentUser.useResults()
    const groupAmendsByKey = group?.amends ?? {}
    const amends = Object.values(groupAmendsByKey)

    return {
        amends,
        loading,
        saveAmend,
        deleteAmend,
    }

    async function saveAmend({ amendsKey, content, internalContent, externalContent, noteMode }) {
        if (!projectId || !groupId) return null
        if (!amendsKey) throw new Error("saveAmend requires amendsKey")

        const now = new Date().toISOString()
        const existing = groupAmendsByKey[amendsKey] ?? null
        const userId = existing?.userId ?? currentUser?.id ?? currentUser?.UserId ?? null
        const userName = existing?.userName ?? getCurrentUserName(currentUser) ?? null
        const nextAmends = {
            ...groupAmendsByKey,
            [amendsKey]: {
                id: existing?.id ?? amendsKey,
                userId,
                userName,
                payload: {
                    amendsKey,
                    content: stripChipHtml(content),
                    internalContent: stripChipHtml(internalContent),
                    externalContent: stripChipHtml(externalContent),
                    noteMode,
                },
                modified: now,
                date: existing?.date ?? now,
            },
        }

        try {
            const updated = await updateProjectGroup({
                projectId,
                group: {
                    id: groupId,
                    amends: nextAmends,
                },
            })
            if (!updated) {
                errorSnackbar("Failed to save author notes")
                return null
            }
            successSnackbar("Author notes saved")
            return nextAmends[amendsKey]
        } catch (error) {
            errorSnackbar("Failed to save author notes")
            throw error
        }
    }

    async function deleteAmend({ amendsKey }) {
        if (!projectId || !groupId || !amendsKey) return null

        const nextAmends = { ...groupAmendsByKey }
        delete nextAmends[amendsKey]

        try {
            const updated = await updateProjectGroup({
                projectId,
                group: {
                    id: groupId,
                    amends: nextAmends,
                },
            })
            if (!updated) {
                errorSnackbar("Failed to delete author notes")
                return null
            }
            successSnackbar("Author notes deleted")
            return true
        } catch (error) {
            errorSnackbar("Failed to delete author notes")
            throw error
        }
    }
}

function getCurrentUserName(currentUser = {}) {
    return currentUser?.metadata?.displayName ?? currentUser?.displayName ?? null
}
